import { BorderOutlined, CodeSandboxOutlined, DragOutlined, ExpandOutlined, QuestionCircleOutlined, RotateRightOutlined, SelectOutlined, ZoomInOutlined, ZoomOutOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DOCK_PARKING_LINE, DOCK_YARD_DEPTH, dockRoom, layoutDock, type DockLayout, type LocationContents, type MapCell, type MapRack, type MapRoom, type MapZone, type WarehouseMap as WarehouseMapData } from "@wms/domain";
import { Alert, App, Button, Descriptions, Drawer, Empty, Form, Input, Modal, Popover, Segmented, Select, Space, Spin, Switch, Tag, Tooltip, Typography } from "antd";
import { Component, lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage } from "../api/client";
import { useAuthStore } from "../auth/store";
import { EntityTable } from "./EntityTable";
import { LoadProblem } from "./LoadProblem";
import type { Focus3D, GateInfo, GateState, Scene3D } from "./Warehouse3D";

// three.js comes with the 3D view only: the 2D plan and the rest of the app do not load it
const Warehouse3D = lazy(() => import("./Warehouse3D"));

/** A browser (or a remote desktop) without WebGL keeps the drawn isometric view. */
function hasWebGL(): boolean {
  try {
    const canvas = document.createElement("canvas");
    return !!(canvas.getContext("webgl2") ?? canvas.getContext("webgl"));
  } catch {
    return false;
  }
}

/**
 * UXW-71 (31.10.2026): the 3D view that fails while loading or drawing (its code did not arrive, the graphics card
 * gave up) hands over to the drawn view with a message, instead of an empty canvas or a broken page.
 */
class SceneBoundary extends Component<{ onError: () => void; children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch() {
    this.props.onError();
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

type Mode = "zone" | "fill" | "state" | "capacity";
type View = "plan" | "iso";

const PX_PER_M = 40; // plan pixels per metre at 100 %
const MIN_STRIP = 6; // px: a level strip inside a rack footprint never gets thinner than this
const MAJOR_M = 5; // metres between the stronger grid lines and the ruler ticks
const RULER = 28; // px margin around the 2D plan that holds the metre rulers
const STORAGE_COLORS = ["#4f8ef7", "#8bc34a", "#795548", "#607d8b", "#e05b8f", "#ff7a59"];
const FLOOR_W = 2.0 * PX_PER_M; // a floor place (receiving, assembly, gate…) drawn inside its zone
const FLOOR_D = 1.6 * PX_PER_M;
const FLOOR_GAP = 0.6 * PX_PER_M;
const FLOOR_H = 1.2 * PX_PER_M; // its height in 3D: one pallet

/** The colour of a zone — on the plan and in the «Зоны» mode — tells what the zone is for: the cold room reads cold,
 * receiving green, assembly orange, expedition violet, quarantine red; storage zones take the blue palette in turn. */
function zoneColorsOf(zones: MapZone[]): Map<string, string> {
  const out = new Map<string, string>();
  let storage = 0;
  for (const z of zones) {
    const tmax = z.temperature_max === null || z.temperature_max === undefined ? null : Number(z.temperature_max);
    let color: string;
    if (tmax !== null && tmax <= 0) color = "#5b6cff";
    else if (tmax !== null && tmax <= 10) color = "#13a8c2";
    else if (z.kind === "receiving") color = "#2fb37a";
    else if (z.kind === "shipping" || z.kind === "packing") color = "#f08c1a";
    else if (z.kind === "expedition" || z.kind === "transit") color = "#9b5de5";
    else if (z.kind === "other") color = "#e0505e";
    else color = STORAGE_COLORS[storage++ % STORAGE_COLORS.length];
    out.set(z.id, color);
  }
  return out;
}

/** «t 2…8 °C» under the name of a zone that keeps a temperature. */
function zoneNote(z: MapZone): string | null {
  const lo = z.temperature_min === null || z.temperature_min === undefined ? null : Number(z.temperature_min);
  const hi = z.temperature_max === null || z.temperature_max === undefined ? null : Number(z.temperature_max);
  if (lo === null && hi === null) return null;
  const t = (v: number) => String(v).replace("-", "−");
  return `t ${lo === null ? "до" : t(lo)}${lo !== null && hi !== null ? "…" : " "}${hi === null ? "и выше" : t(hi)} °C`;
}

interface ZoneArea {
  zone: MapZone;
  color: string;
  x: number; // px on the plan
  y: number;
  w: number;
  h: number;
}

interface FloorPlace {
  cell: MapCell;
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Placed {
  rack: MapRack;
  x: number; // px, top-left corner of the footprint on the plan
  y: number;
  w: number; // px, positions × cell length
  cw: number; // px per position
  realD: number; // px, real depth of the rack
  d: number; // px, depth as drawn on the 2D plan (never thinner than levels × MIN_STRIP)
  h: number; // px, levels × cell height (3D)
  auto: boolean; // laid out automatically because the rack has no coordinates
}

interface RoomRect {
  room: MapRoom;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** «R01 · A01»: rack and aisle codes as shown above a rack. */
const rackLabel = (rack: { code: string; aisle_code: string | null }) => `${rack.code}${rack.aisle_code ? ` · ${rack.aisle_code}` : ""}`;

/** Labels never run over the neighbour rack: cut to the width (≈ 6.6 px per character at 11 px), full text in the tooltip. */
const fitLabel = (text: string, widthPx: number, pxPerChar = 6.6): string => {
  const max = Math.max(3, Math.floor(widthPx / pxPerChar));
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
};

const m = (v: string | number | null | undefined): number => (v === null || v === undefined ? 0 : Number(v));
const fmtM = (v: string | number | null | undefined): string => m(v).toLocaleString("ru-RU", { maximumFractionDigits: 2 });

function cellFill(cell: MapCell, mode: Mode, zoneColors: Map<string, string>, states: WarehouseMapData["states"], maxQty: number): string {
  if (!cell.is_active) return "var(--wms-cell-blocked)";
  const qty = Number(cell.quantity);
  if (mode === "zone") {
    if (!cell.zone_id) return qty > 0 ? "var(--wms-cell-na)" : "var(--wms-cell-empty)";
    const color = zoneColors.get(cell.zone_id) ?? STORAGE_COLORS[0];
    return qty > 0 ? color : color + "55";
  }
  if (mode === "capacity") {
    const p = cell.fill_percent;
    if (p === null || p === undefined) return qty > 0 || cell.nested > 0 ? "var(--wms-cell-na)" : "var(--wms-cell-empty)";
    if (p >= 100) return "#cf1322";
    if (p >= 80) return "#fa8c16";
    if (p > 0) return `hsl(${120 - Math.round(p * 0.6)}, 70%, ${70 - Math.round(p * 0.25)}%)`;
    return "var(--wms-cell-empty)";
  }
  if (mode === "state") {
    const state = states.find((s) => s.id === cell.state_id);
    if (!state) return qty > 0 ? "#91caff" : "var(--wms-cell-empty)";
    if (!state.allow_put && !state.allow_pick) return "#ff4d4f";
    if (!state.allow_put || !state.allow_pick) return "#faad14";
    return qty > 0 ? "#52c41a" : "#d9f7be";
  }
  // «Заполненность»: blocked light red, free light green, stock blue (darker = more), all reserved orange
  const state = states.find((s) => s.id === cell.state_id);
  if (state && !state.allow_put && !state.allow_pick) return "var(--wms-cell-blocked)";
  if (qty <= 0) return "var(--wms-cell-free)";
  const reserved = Number(cell.planned_out);
  if (reserved >= qty) return "#fa8c16";
  const t = Math.min(1, Math.log1p(qty) / Math.log1p(Math.max(maxQty, 1)));
  const light = Math.round(230 - 120 * t);
  return `rgb(${light - 90}, ${light - 40}, 255)`;
}

interface Rect {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** Rotate a plan rectangle by k·90° inside a scene of size W×H; rectangles stay axis-aligned. */
function rotateRect(r: Rect, k: number, W: number, H: number): Rect {
  const pts = [
    [r.x0, r.y0],
    [r.x1, r.y1],
  ].map(([x, y]) => {
    switch (k % 4) {
      case 1:
        return [H - y, x];
      case 2:
        return [W - x, H - y];
      case 3:
        return [y, W - x];
      default:
        return [x, y];
    }
  });
  return { x0: Math.min(pts[0][0], pts[1][0]), y0: Math.min(pts[0][1], pts[1][1]), x1: Math.max(pts[0][0], pts[1][0]), y1: Math.max(pts[0][1], pts[1][1]) };
}

function iso(x: number, y: number, z: number): [number, number] {
  return [(x - y) * 0.866, (x + y) * 0.5 - z];
}

function poly(points: [number, number][], dx: number, dy: number): string {
  return points.map(([x, y]) => `${(x + dx).toFixed(1)},${(y + dy).toFixed(1)}`).join(" ");
}

interface HoverInfo {
  cell: MapCell;
  rack?: MapRack;
  x: number;
  y: number;
  /** A gate of a dock: «Ворота 3 · Пандус 1» and its slot now. */
  label?: string;
  note?: string;
}

function HoverCard({ hover, zoneName, stateName }: { hover: HoverInfo; zoneName: (id: string | null) => string; stateName: (id: string | null) => string }) {
  const r = hover.rack;
  return (
    <div style={{ position: "fixed", left: hover.x + 12, top: hover.y + 12, background: "rgba(17,24,39,.94)", color: "#fff", padding: "8px 12px", borderRadius: 10, fontSize: 12, lineHeight: 1.5, pointerEvents: "none", zIndex: 1000, boxShadow: "0 8px 24px rgba(0,0,0,.25)" }}>
      {hover.label && <div style={{ fontWeight: 600 }}>{hover.label}</div>}
      <div style={{ fontWeight: 600 }}>{hover.cell.code}</div>
      <div>зона: {zoneName(hover.cell.zone_id)}</div>
      <div>состояние: {stateName(hover.cell.state_id)}</div>
      <div>
        остаток: {Number(hover.cell.quantity)}
        {Number(hover.cell.planned_out) ? ` (резерв ${Number(hover.cell.planned_out)})` : ""}
        {hover.cell.objects ? `, товаров: ${hover.cell.objects}` : ""}
      </div>
      {hover.cell.nested > 0 && <div>вложенных МХ: {hover.cell.nested}</div>}
      {hover.cell.fill_percent !== null && <div>заполнено: {hover.cell.fill_percent}%</div>}
      {hover.note && <div>{hover.note}</div>}
      {r && (
        <div style={{ opacity: 0.8 }}>
          размер ячейки: {fmtM(r.cell_length)} × {fmtM(r.cell_width)} × {fmtM(r.cell_height)} м{r.sized_by_type ? "" : " (по умолчанию)"}
        </div>
      )}
    </div>
  );
}

/** A point of a dock in plan metres: `t` along the wall from the centre of the row, `s` out to the yard. */
function dockPoint(l: DockLayout, t: number, s: number): [number, number] {
  return [l.center[0] + l.along[0] * t + l.normal[0] * s, l.center[1] + l.along[1] * t + l.normal[1] * s];
}

/**
 * ТЗ 4.3: on the 2D plan a ramp is a grey strip outside the wall, a gate is a gap in the wall with its number, a slope
 * is hatched with an arrow down the incline. A click on a gate opens its contents like any floor place.
 */
function DockPlan({ layout, cells, onHover, onOpen }: { layout: DockLayout; cells: Record<string, MapCell>; onHover: (h: HoverInfo | null) => void; onOpen: (cell: MapCell) => void }) {
  const px = (t: number, s: number) => dockPoint(layout, t, s).map((v) => v * PX_PER_M) as [number, number];
  const box = (t0: number, t1: number, s0: number, s1: number) => {
    const [ax, ay] = px(t0, s0);
    const [bx, by] = px(t1, s1);
    return { x: Math.min(ax, bx), y: Math.min(ay, by), width: Math.abs(ax - bx), height: Math.abs(ay - by) };
  };
  const half = layout.rowLength / 2 + 1;
  const depth = layout.rampDepth;
  const slope = layout.dock.slope;
  const t0 = -half - (slope === "left" ? layout.slopeLength : 0);
  const t1 = half + (slope === "right" ? layout.slopeLength : 0);
  const lines = Array.from(new Set(layout.gates.flatMap((g) => [g.t - layout.pitch / 2, g.t + layout.pitch / 2].map((t) => Math.round(t * 100) / 100))));
  const sign = slope === "right" ? 1 : -1;
  const [ax, ay] = px(sign * (half + 0.6), 1.5);
  const [bx, by] = px(sign * (half + layout.slopeLength - 0.6), 1.5);
  const [lx, ly] = px(-half, depth + 1.2);
  const wallFrom = px(-half, 0);
  const wallTo = px(half, 0);
  return (
    <g data-testid={`dock-plan-${layout.dock.code}`}>
      <rect {...box(t0, t1, depth, depth + DOCK_YARD_DEPTH)} fill="var(--wms-3d-yard)" fillOpacity={0.12} pointerEvents="none" />
      {lines.map((t) => {
        const [x1, y1] = px(t, depth);
        const [x2, y2] = px(t, depth + DOCK_PARKING_LINE);
        return <line key={t} x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--wms-rack-line)" strokeWidth={1.5} pointerEvents="none" />;
      })}
      {depth > 0 && <rect {...box(-half, half, 0, depth)} fill="var(--wms-3d-dock)" fillOpacity={0.6} stroke="var(--wms-rack-line)" pointerEvents="none" />}
      {slope !== "none" && (
        <g pointerEvents="none">
          <rect {...box(sign * half, sign * (half + layout.slopeLength), 0, 3)} fill="url(#dockHatch)" stroke="var(--wms-rack-line)" strokeDasharray="6 4" />
          <line x1={ax} y1={ay} x2={bx} y2={by} stroke="var(--wms-text)" strokeWidth={1.5} markerEnd="url(#dockArrow)" />
        </g>
      )}
      <line x1={wallFrom[0]} y1={wallFrom[1]} x2={wallTo[0]} y2={wallTo[1]} stroke="var(--wms-text)" strokeWidth={4} pointerEvents="none" />
      {layout.gates.map((g) => {
        const cell = cells[g.gate.location_id];
        const [nx, ny] = px(g.t, depth > 0 ? depth / 2 + 0.15 : 0.9);
        const hover = (e: React.MouseEvent) => cell && onHover({ cell, x: e.clientX, y: e.clientY, label: `Ворота ${g.gate.number} · ${layout.dock.name}` });
        return (
          <g key={g.gate.location_id} style={{ cursor: cell ? "pointer" : undefined }} onMouseEnter={hover} onMouseMove={hover} onMouseLeave={() => onHover(null)} onClick={() => cell && onOpen(cell)}>
            <rect {...box(g.t - layout.doorWidth / 2, g.t + layout.doorWidth / 2, -0.25, 0.25)} fill="var(--wms-surface)" stroke="var(--wms-primary)" strokeWidth={2} />
            <text x={nx} y={ny + 6} fontSize={18} fontWeight={700} fill="var(--wms-primary)" textAnchor="middle" stroke="var(--wms-surface)" strokeWidth={4} paintOrder="stroke">
              {g.gate.number}
            </text>
          </g>
        );
      })}
      <text x={lx} y={ly} fontSize={12} fontWeight={600} fill="var(--wms-muted)" textAnchor="middle" pointerEvents="none">
        {layout.dock.name}
      </text>
    </g>
  );
}

/** Interactive plan: racks on the floor, cells inside, colours by zone / fill / state; drag racks to arrange. */
/** `warehouseId` — the warehouse the plan shows (BUG-02: every warehouse, not only the first one). */
export function WarehouseMap({ warehouseId }: { warehouseId?: string }) {
  const can = useAuthStore((s) => s.can);
  const default3d = useAuthStore((s) => s.option("use_3d"));
  // 3D docks (ТЗ 29.10.2026): ramps and gates on the plan; the camera flies to a chosen zone, cell or gate
  const docksOn = useAuthStore((s) => s.option("use_docks"));
  const focusOn = useAuthStore((s) => s.option("use_3d_focus"));
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const [roomId, setRoomId] = useState<string | undefined>();
  const [mode, setMode] = useState<Mode>("fill");
  const [view, setView] = useState<View>(() => (default3d ? "iso" : "plan"));
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [arrange, setArrange] = useState(false);
  // UX-04: pick several cells with plain clicks, no Shift needed
  const [selectMode, setSelectMode] = useState(false);
  const [showZones, setShowZones] = useState(true);
  const [hover, setHover] = useState<HoverInfo | null>(null);
  const [selected, setSelected] = useState<{ cell: MapCell; rack?: MapRack } | null>(null);
  const [webgl, setWebgl] = useState(hasWebGL);
  // UXW-71: why the 3D model is not shown although the browser has WebGL — it did not start, lost its context, failed
  const [sceneFailure, setSceneFailure] = useState<"start" | "lost" | "error" | null>(null);
  const dropScene = (why: "start" | "lost" | "error") => {
    setSceneFailure(why);
    setWebgl(false);
  };
  const real3d = view === "iso" && webgl;
  // the address search in 3D: the camera flies to the cell and pins its number
  const [focus, setFocus] = useState<Focus3D | null>(null);
  const [dragging, setDragging] = useState<{ id: string; dx: number; dy: number; startX: number; startY: number } | null>(null);
  const [offsets, setOffsets] = useState<Record<string, { x: number; y: number }>>({});
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [marquee, setMarquee] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  // REQ-13: the previous position of the last moved rack, so a slip of the hand is one click to undo
  const [lastMove, setLastMove] = useState<{ id: string; code: string; x: number | null; y: number | null } | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm] = Form.useForm();
  const svgRef = useRef<SVGSVGElement>(null);

  const map = useQuery({
    queryKey: ["topology", "map", warehouseId ?? "default", roomId ?? "all"],
    queryFn: () => api.get<WarehouseMapData>("/topology/map", { room_id: roomId, warehouse_id: warehouseId }),
  });
  const contentsQuery = useQuery({
    queryKey: ["locations", "contents", selected?.cell.id],
    queryFn: () => api.get<LocationContents>(`/locations/${selected!.cell.id}/contents`),
    enabled: !!selected,
  });
  const bulk = useMutation({
    mutationFn: (values: Record<string, unknown>) => {
      const { clear_color, ...rest } = values;
      const data = Object.fromEntries(Object.entries(rest).filter(([, v]) => v !== undefined && v !== null && v !== ""));
      if (clear_color) data.color = null;
      return api.post<{ updated: number }>("/locations/bulk-update", { ids: [...picked], data });
    },
    onSuccess: (r) => {
      message.success(`Изменено ячеек: ${r.updated}`);
      setBulkOpen(false);
      setPicked(new Set());
      queryClient.invalidateQueries({ queryKey: ["topology", "map"] });
      queryClient.invalidateQueries({ queryKey: ["locations"] });
    },
    onError: (e) => message.error(errorMessage(e)),
  });
  const save = useMutation({
    mutationFn: ({ id, x, y }: { id: string; x: number | null; y: number | null }) =>
      api.patch(`/racks/${id}`, { x: x === null ? null : x.toFixed(1), y: y === null ? null : y.toFixed(1) }),
    onSuccess: (_data, vars) => {
      message.success(vars.x === null ? "Положение стеллажа сброшено" : `Стеллаж поставлен: ${vars.x.toFixed(1)} × ${vars.y!.toFixed(1)} м`);
      queryClient.invalidateQueries({ queryKey: ["topology", "map"] });
    },
    onError: (e) => message.error(errorMessage(e)),
  });
  useEffect(() => {
    if (!dragging) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDragging(null); // drop the rack where it was
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dragging]);

  const zoneColors = useMemo(() => zoneColorsOf(map.data?.zones ?? []), [map.data]);
  const maxQty = useMemo(() => Math.max(0, ...(map.data?.racks.flatMap((r) => r.cells.map((c) => Number(c.quantity))) ?? [])), [map.data]);

  // Rooms with a floor size become rectangles on the plan (metres → px).
  const roomRects = useMemo<RoomRect[]>(
    () =>
      (map.data?.rooms ?? [])
        .filter((r) => m(r.length) > 0 && m(r.width) > 0 && (!roomId || r.id === roomId))
        .map((r) => ({ room: r, x: m(r.x) * PX_PER_M, y: m(r.y) * PX_PER_M, w: m(r.length) * PX_PER_M, h: m(r.width) * PX_PER_M })),
    [map.data, roomId],
  );

  // Racks with coordinates stand where they are (metres); the rest are laid out row by row inside their room.
  const placed = useMemo<Placed[]>(() => {
    const racks = map.data?.racks ?? [];
    const rooms = map.data?.rooms ?? [];
    const size = (r: MapRack) => {
      const cw = m(r.cell_length) * PX_PER_M;
      const realD = (r.depth ? m(r.depth) : m(r.cell_width)) * PX_PER_M;
      return { cw, w: r.positions * cw, realD, d: Math.max(realD, r.levels * MIN_STRIP), h: r.levels * m(r.cell_height) * PX_PER_M };
    };
    const out: Placed[] = [];
    const bottomByRoom = new Map<string, number>();
    for (const r of racks) {
      if (r.x !== null && r.y !== null) {
        const sz = size(r);
        const x = m(r.x) * PX_PER_M;
        const y = m(r.y) * PX_PER_M;
        out.push({ rack: r, x, y, ...sz, auto: false });
        bottomByRoom.set(r.room_id, Math.max(bottomByRoom.get(r.room_id) ?? 0, y + sz.d));
      }
    }
    const groups = new Map<string, MapRack[]>();
    for (const r of racks) {
      if (r.x === null || r.y === null) {
        const key = `${r.room_id}|${r.aisle_code ?? ""}`;
        groups.set(key, [...(groups.get(key) ?? []), r]);
      }
    }
    const cursor = new Map<string, number>();
    for (const [key, list] of groups) {
      const rid = key.split("|")[0];
      const room = rooms.find((rm) => rm.id === rid);
      const ox = m(room?.x) * PX_PER_M;
      const oy = m(room?.y) * PX_PER_M;
      const limit = m(room?.length) > 0 ? ox + m(room?.length) * PX_PER_M : Infinity;
      let y = cursor.get(rid) ?? (bottomByRoom.has(rid) ? bottomByRoom.get(rid)! + 2 * PX_PER_M : oy + PX_PER_M);
      let x = ox + PX_PER_M;
      let rowD = 0;
      for (const r of list) {
        const sz = size(r);
        if (x > ox + PX_PER_M && x + sz.w > limit) {
          x = ox + PX_PER_M;
          y += rowD + 1.5 * PX_PER_M;
          rowD = 0;
        }
        out.push({ rack: r, x, y, ...sz, auto: true });
        x += sz.w + PX_PER_M;
        rowD = Math.max(rowD, sz.d);
      }
      cursor.set(rid, y + rowD + 1.5 * PX_PER_M);
    }
    return out;
  }, [map.data]);

  // Zones as named, tinted areas over the plan: the area set in the zone card, otherwise the racks of its cells.
  // Larger areas first, so a cold room inside the storage hall lies on top of it.
  const zoneAreas = useMemo<ZoneArea[]>(() => {
    const out: ZoneArea[] = [];
    for (const z of map.data?.zones ?? []) {
      const color = zoneColors.get(z.id) ?? STORAGE_COLORS[0];
      if (m(z.length) > 0 && m(z.width) > 0) {
        out.push({ zone: z, color, x: m(z.x) * PX_PER_M, y: m(z.y) * PX_PER_M, w: m(z.length) * PX_PER_M, h: m(z.width) * PX_PER_M });
        continue;
      }
      const mine = placed.filter((p) => p.rack.cells.some((c) => c.zone_id === z.id));
      if (!mine.length) continue;
      const pad = 0.6 * PX_PER_M;
      const x0 = Math.min(...mine.map((p) => p.x)) - pad;
      const y0 = Math.min(...mine.map((p) => p.y)) - pad - 1.4 * PX_PER_M; // room for the name above the racks
      const x1 = Math.max(...mine.map((p) => p.x + p.w)) + pad;
      const y1 = Math.max(...mine.map((p) => p.y + p.d)) + pad;
      out.push({ zone: z, color, x: Math.max(0, x0), y: Math.max(0, y0), w: x1 - Math.max(0, x0), h: y1 - Math.max(0, y0) });
    }
    return out.sort((a, b) => b.w * b.h - a.w * a.h);
  }, [map.data, placed, zoneColors]);

  const baseWidth = Math.max(
    400,
    ...placed.map((p) => p.x + (offsets[p.rack.id]?.x ?? 0) + p.w + PX_PER_M),
    ...roomRects.map((r) => r.x + r.w + PX_PER_M / 2),
    ...zoneAreas.map((a) => a.x + a.w + PX_PER_M / 2),
  );
  const baseHeight = Math.max(
    240,
    ...placed.map((p) => p.y + (offsets[p.rack.id]?.y ?? 0) + p.d + PX_PER_M),
    ...roomRects.map((r) => r.y + r.h + PX_PER_M / 2),
    ...zoneAreas.map((a) => a.y + a.h + PX_PER_M / 2),
  );

  // 3D docks (ТЗ 3.3): the row of gates on the room wall nearest to its shipping zone, in metres; one layout for the
  // plan, the isometry and the 3D model. Without the option the gates stay floor places of their zone, as before.
  const dockLayouts = useMemo<DockLayout[]>(() => {
    if (!docksOn || !map.data?.docks?.length) return [];
    const M = (px: number) => px / PX_PER_M;
    const rooms = roomRects.map((r) => ({ id: r.room.id, x: M(r.x), y: M(r.y), length: M(r.w), width: M(r.h) }));
    const plan = { id: "plan", x: 0, y: 0, length: M(baseWidth), width: M(baseHeight) };
    const out: DockLayout[] = [];
    for (const dock of map.data.docks) {
      const area = zoneAreas.find((a) => a.zone.id === dock.zone_id);
      const zone = area ? { x: M(area.x), y: M(area.y), length: M(area.w), width: M(area.h) } : null;
      const layout = layoutDock(dock, dockRoom(dock, rooms, zone) ?? plan, zone);
      if (layout) out.push(layout);
    }
    return out;
  }, [docksOn, map.data, roomRects, zoneAreas, baseWidth, baseHeight]);
  const docked = useMemo(() => new Set(dockLayouts.flatMap((l) => l.gates.map((g) => g.gate.location_id))), [dockLayouts]);

  // Floor places (receiving, assembly, loading, quarantine, gates) stand in rows inside the area of their zone.
  const floorPlaces = useMemo<FloorPlace[]>(() => {
    const byZone = new Map<string, MapCell[]>();
    for (const c of map.data?.loose ?? []) if (c.zone_id && !docked.has(c.id)) byZone.set(c.zone_id, [...(byZone.get(c.zone_id) ?? []), c]);
    const out: FloorPlace[] = [];
    for (const area of zoneAreas) {
      const cells = (byZone.get(area.zone.id) ?? []).slice().sort((a, b) => a.code.localeCompare(b.code, "ru", { numeric: true }));
      const left = area.x + 0.8 * PX_PER_M;
      let x = left;
      let y = area.y + 2.8 * PX_PER_M; // under the name of the zone
      for (const c of cells) {
        if (x > left && x + FLOOR_W > area.x + area.w - 0.4 * PX_PER_M) {
          x = left;
          y += FLOOR_D + FLOOR_GAP;
        }
        out.push({ cell: c, x, y, w: FLOOR_W, h: FLOOR_D });
        x += FLOOR_W + FLOOR_GAP;
      }
    }
    return out;
  }, [map.data, zoneAreas, docked]);
  const unplacedLoose = (map.data?.loose ?? []).filter((c) => !docked.has(c.id) && !floorPlaces.some((f) => f.cell.id === c.id));

  // the ramps and yards stand outside the rooms: the plan grows to show them, to the left and up as well
  const dockBox = dockLayouts.length
    ? {
        x0: Math.min(...dockLayouts.map((l) => l.bounds.x0)) * PX_PER_M,
        y0: Math.min(...dockLayouts.map((l) => l.bounds.y0)) * PX_PER_M,
        x1: Math.max(...dockLayouts.map((l) => l.bounds.x1)) * PX_PER_M,
        y1: Math.max(...dockLayouts.map((l) => l.bounds.y1)) * PX_PER_M,
      }
    : null;
  const width = Math.max(baseWidth, dockBox ? dockBox.x1 + PX_PER_M / 2 : 0);
  const height = Math.max(baseHeight, dockBox ? dockBox.y1 + PX_PER_M / 2 : 0);
  const padX = dockBox && dockBox.x0 < 0 ? Math.ceil(-dockBox.x0 + PX_PER_M / 2) : 0;
  const padY = dockBox && dockBox.y0 < 0 ? Math.ceil(-dockBox.y0 + PX_PER_M / 2) : 0;
  const gateCells = useMemo(() => Object.fromEntries((map.data?.loose ?? []).filter((c) => docked.has(c.id)).map((c) => [c.id, c])), [map.data, docked]);
  const defaultSized = placed.filter((p) => !p.rack.sized_by_type).length;
  const currentRoom = roomId ? map.data?.rooms.find((r) => r.id === roomId) : undefined;

  // Isometric scene: every rack becomes a row of boxes (one per position) stacked by level, all in metres.
  const isoScene = useMemo(() => {
    if (view !== "iso" || webgl || !map.data) return null;
    const W = width;
    const H = height;
    type Box = { cell: MapCell; rack?: MapRack; rect: Rect; z0: number; z1: number };
    // every rack stands as a frame with its levels and bays — an empty rack too, so the racked area reads at a glance;
    // the cells that exist are drawn into it. Racks and floor places are painted from the back to the front.
    type Item =
      | { kind: "rack"; rack: MapRack; rect: Rect; height: number; alongX: boolean; boxes: Box[] }
      | { kind: "floor"; box: Box };
    const items: Item[] = [];
    const racks: { rack: MapRack; rect: Rect; height: number }[] = [];
    for (const p of placed) {
      const off = offsets[p.rack.id] ?? { x: 0, y: 0 };
      const x = p.x + off.x;
      const y = p.y + off.y;
      const foot = rotateRect({ x0: x, y0: y, x1: x + p.w, y1: y + p.realD }, rotation, W, H);
      racks.push({ rack: p.rack, rect: foot, height: p.h });
      const lh = p.h / Math.max(1, p.rack.levels);
      const boxes: Box[] = [];
      for (const c of p.rack.cells) {
        const col = (c.position ?? 1) - 1;
        const lvl = (c.level ?? 1) - 1;
        const rect = rotateRect({ x0: x + col * p.cw, y0: y, x1: x + (col + 1) * p.cw, y1: y + p.realD }, rotation, W, H);
        boxes.push({ cell: c, rack: p.rack, rect, z0: lvl * lh, z1: (lvl + 1) * lh });
      }
      boxes.sort((a, b) => a.rect.x0 + a.rect.y0 - (b.rect.x0 + b.rect.y0) || a.z0 - b.z0);
      items.push({ kind: "rack", rack: p.rack, rect: foot, height: p.h, alongX: rotation % 2 === 0, boxes });
    }
    for (const f of floorPlaces) {
      const rect = rotateRect({ x0: f.x, y0: f.y, x1: f.x + f.w, y1: f.y + f.h }, rotation, W, H);
      items.push({ kind: "floor", box: { cell: f.cell, rect, z0: 0, z1: FLOOR_H } });
    }
    const depth = (it: Item) => (it.kind === "rack" ? it.rect.x0 + it.rect.y0 : it.box.rect.x0 + it.box.rect.y0);
    items.sort((a, b) => depth(a) - depth(b));
    const floors = roomRects.map((r) => ({ room: r.room, rect: rotateRect({ x0: r.x, y0: r.y, x1: r.x + r.w, y1: r.y + r.h }, rotation, W, H) }));
    // ТЗ 4.3: in the drawn isometry a ramp is a box and a gate a rectangle on the wall with its number, no animation
    const docks = dockLayouts.map((l) => {
      const at = (t: number, s: number) => dockPoint(l, t, s).map((v) => v * PX_PER_M);
      const flat = (t0: number, t1: number, s0: number, s1: number) => {
        const [x0, y0] = at(t0, s0);
        const [x1, y1] = at(t1, s1);
        return rotateRect({ x0, y0, x1, y1 }, rotation, W, H);
      };
      const half = l.rowLength / 2 + 1;
      return {
        layout: l,
        ramp: l.rampDepth > 0 ? flat(-half, half, 0, l.rampDepth) : null,
        edge: flat(-half, half, l.rampDepth, l.rampDepth),
        doors: l.gates.map((g) => ({ gate: g.gate, seg: flat(g.t - l.doorWidth / 2, g.t + l.doorWidth / 2, 0, 0) })),
      };
    });
    const dockPoints: [number, number][] = docks.flatMap((d) => [
      ...(d.ramp ? [iso(d.ramp.x0, d.ramp.y0, 0), iso(d.ramp.x1, d.ramp.y1, 0), iso(d.ramp.x0, d.ramp.y1, 0), iso(d.ramp.x1, d.ramp.y0, 0)] : []),
      iso(d.edge.x0, d.edge.y0, -d.layout.rampHeight * PX_PER_M),
      iso(d.edge.x1, d.edge.y1, -d.layout.rampHeight * PX_PER_M),
      ...d.doors.flatMap(({ seg }) => [iso(seg.x0, seg.y0, (d.layout.doorHeight + 1) * PX_PER_M), iso(seg.x1, seg.y1, (d.layout.doorHeight + 1) * PX_PER_M)]),
    ]);
    const zoneFloors = zoneAreas.map((a) => ({ area: a, rect: rotateRect({ x0: a.x, y0: a.y, x1: a.x + a.w, y1: a.y + a.h }, rotation, W, H) }));
    racks.sort((a, b) => a.rect.x0 + a.rect.y0 - (b.rect.x0 + b.rect.y0));
    const sceneW = rotation % 2 ? H : W;
    const sceneH = rotation % 2 ? W : H;
    const corners: [number, number][] = [iso(0, 0, 0), iso(sceneW, 0, 0), iso(sceneW, sceneH, 0), iso(0, sceneH, 0)];
    const maxZ = Math.max(PX_PER_M, ...racks.map((r) => r.height));
    const minX = Math.min(...corners.map((c) => c[0]), ...dockPoints.map((c) => c[0])) - 20;
    const minY = Math.min(...corners.map((c) => c[1] - maxZ), ...dockPoints.map((c) => c[1])) - 30;
    const maxX = Math.max(...corners.map((c) => c[0]), ...dockPoints.map((c) => c[0])) + 20;
    const maxY = Math.max(...corners.map((c) => c[1]), ...dockPoints.map((c) => c[1])) + 20;
    return { items, racks, floors, zoneFloors, docks, corners, sceneW, sceneH, dx: -minX, dy: -minY, w: maxX - minX, h: maxY - minY };
  }, [view, webgl, map.data, placed, roomRects, zoneAreas, floorPlaces, offsets, rotation, width, height, dockLayouts]);

  // the 3D model in metres and the colour of every cell by the current mode
  const scene3d = useMemo<Scene3D | null>(() => {
    if (!real3d || !map.data) return null;
    const M = (px: number) => px / PX_PER_M;
    return {
      width: M(width),
      depth: M(height),
      racks: placed.map((p) => {
        const off = offsets[p.rack.id] ?? { x: 0, y: 0 };
        return { rack: p.rack, x: M(p.x + off.x), y: M(p.y + off.y), length: M(p.w), depth: M(p.realD), height: M(p.h) };
      }),
      floor: floorPlaces.map((f) => ({ cell: f.cell, x: M(f.x), y: M(f.y), length: M(f.w), depth: M(f.h), height: M(FLOOR_H) })),
      zones: zoneAreas.map((a) => ({ zone: a.zone, color: a.color, note: zoneNote(a.zone), x: M(a.x), y: M(a.y), length: M(a.w), depth: M(a.h) })),
      rooms: roomRects.map((r) => ({ room: r.room, x: M(r.x), y: M(r.y), length: M(r.w), depth: M(r.h) })),
      docks: dockLayouts,
      gateCells,
    };
  }, [real3d, map.data, placed, offsets, floorPlaces, zoneAreas, roomRects, width, height, dockLayouts, gateCells]);

  // ТЗ 4.2: the doors and lamps of the gates follow today's dock schedule (read only, once a minute); without the right
  // to see the schedule the gates stay closed and dark, an inactive place still shows red
  const canSlots = can("inbound.dock_slots.read");
  const today = new Date();
  const todayIso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const slots = useQuery({
    queryKey: ["dock-schedule", "map", map.data?.warehouse_id, todayIso],
    queryFn: () =>
      api.get<{ gates: { id: string; slots: { status: string; vehicle: string | null; document_number: string | null; planned_from: string; planned_to: string }[] }[] }>(
        "/dock-slots/schedule",
        { date: todayIso, warehouse_id: map.data!.warehouse_id },
      ),
    enabled: real3d && dockLayouts.length > 0 && canSlots && !!map.data,
    refetchInterval: 60_000,
  });
  const gateInfo = useMemo(() => {
    const out = new Map<string, GateInfo>();
    const hm = (iso: string) => new Date(iso).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
    const words: Record<GateState, string> = { free: "свободны", arrived: "машина у ворот", working: "идёт погрузка или выгрузка", inactive: "ворота неактивны" };
    for (const id of docked) {
      const row = slots.data?.gates.find((g) => g.id === id);
      const working = row?.slots.find((x) => x.status === "in_progress");
      const arrived = row?.slots.find((x) => x.status === "arrived");
      const slot = working ?? arrived;
      const state: GateState = gateCells[id] && !gateCells[id].is_active ? "inactive" : working ? "working" : arrived ? "arrived" : "free";
      const note = state === "free" && !canSlots ? undefined : [words[state], slot?.vehicle ? `ТС ${slot.vehicle}` : null, slot?.document_number ?? null, slot ? `${hm(slot.planned_from)}–${hm(slot.planned_to)}` : null].filter(Boolean).join(" · ");
      out.set(id, { state, note });
    }
    return out;
  }, [docked, gateCells, slots.data, canSlots]);
  const colors3d = useMemo(() => {
    const out = new Map<string, string>();
    if (!real3d || !map.data) return out;
    for (const c of [...map.data.racks.flatMap((r) => r.cells), ...map.data.loose]) out.set(c.id, cellFill(c, mode, zoneColors, map.data.states, maxQty));
    return out;
  }, [real3d, map.data, mode, zoneColors, maxQty]);

  // Fit the whole plan into the canvas: once when the data arrives and on demand.
  const canvasRef = useRef<HTMLDivElement>(null);
  const fittedRef = useRef<string | null>(null);
  const zoomKey = `wms-map-zoom:${warehouseId ?? "default"}:${view}`;
  /** Fit the plan into the canvas; `readable` keeps at least 70 % so cell codes stay legible (UX-04). */
  const fit = (readable = false) => {
    const sceneW = view === "iso" ? isoScene?.w : width + RULER + padX;
    if (!sceneW) return;
    const avail = (canvasRef.current?.clientWidth ?? 1400) - 8;
    const fitted = Math.min(1.5, Math.max(0.2, Math.floor((avail / sceneW) * 20) / 20));
    setZoom(readable ? Math.max(0.7, fitted) : fitted);
  };
  // the chosen scale is remembered per warehouse and view, so it never resets unexpectedly. It is saved only once
  // the current view has been fitted: switching 2D → 3D must not store the 2D scale under the 3D key (the 3D scene
  // then opened at 100 % with the racks outside the window and looked empty — deep testing report, section 5).
  useEffect(() => {
    if (fittedRef.current !== `${view}:${roomId ?? "all"}`) return;
    try {
      localStorage.setItem(zoomKey, String(zoom));
    } catch {
      /* ignore */
    }
  }, [zoom, zoomKey, view, roomId]);
  useEffect(() => {
    if (!map.data || placed.length === 0) return;
    const key = `${view}:${roomId ?? "all"}`;
    if (fittedRef.current === key) return;
    fittedRef.current = key;
    let saved: number | null = null;
    try {
      const raw = localStorage.getItem(zoomKey);
      saved = raw ? Number(raw) : null;
    } catch {
      saved = null;
    }
    if (saved && saved >= 0.2 && saved <= 3) setZoom(saved);
    else fit(view === "plan"); // the plan keeps cell codes legible; the 3D scene is shown whole
    canvasRef.current?.scrollTo({ left: 0, top: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map.data, placed.length, view, roomId, isoScene?.w, width]);

  const onPointerDown = (p: Placed, e: React.PointerEvent) => {
    if (!arrange) return;
    e.preventDefault();
    // the pointer stays with the plan even when it leaves the rack or the window: no «jumping» racks
    try {
      svgRef.current?.setPointerCapture(e.pointerId);
    } catch {
      /* older browsers */
    }
    setDragging({ id: p.rack.id, dx: 0, dy: 0, startX: e.clientX, startY: e.clientY });
  };
  /** Snap to half a metre and keep the rack inside its room when the room has a floor size. */
  const settle = (p: Placed, rawX: number, rawY: number): [number, number] => {
    const step = PX_PER_M / 2;
    let nx = Math.round(Math.max(0, rawX) / step) * step;
    let ny = Math.round(Math.max(0, rawY) / step) * step;
    const room = roomRects.find((r) => r.room.id === p.rack.room_id);
    if (room) {
      nx = Math.min(Math.max(nx, room.x), Math.max(room.x, room.x + room.w - p.w));
      ny = Math.min(Math.max(ny, room.y), Math.max(room.y, room.y + room.h - p.d));
    }
    return [nx, ny];
  };
  const svgPoint = (e: React.PointerEvent): [number, number] => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return [0, 0];
    return [(e.clientX - rect.left) / zoom - RULER - padX, (e.clientY - rect.top) / zoom - RULER - padY];
  };
  const onBackgroundDown = (e: React.PointerEvent) => {
    if (arrange || view !== "plan") return;
    const [x, y] = svgPoint(e);
    setMarquee({ x0: x, y0: y, x1: x, y1: y });
    if (!e.shiftKey && !e.ctrlKey && !e.metaKey) setPicked(new Set());
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (marquee) {
      const [x, y] = svgPoint(e);
      setMarquee({ ...marquee, x1: x, y1: y });
      return;
    }
    if (!dragging) return;
    setDragging({ ...dragging, dx: (e.clientX - dragging.startX) / zoom, dy: (e.clientY - dragging.startY) / zoom });
  };
  const finishMarquee = () => {
    if (!marquee) return;
    const x0 = Math.min(marquee.x0, marquee.x1);
    const x1 = Math.max(marquee.x0, marquee.x1);
    const y0 = Math.min(marquee.y0, marquee.y1);
    const y1 = Math.max(marquee.y0, marquee.y1);
    if (x1 - x0 > 4 && y1 - y0 > 4) {
      const next = new Set(picked);
      for (const p of placed) {
        const off = offsets[p.rack.id] ?? { x: 0, y: 0 };
        const sh = p.d / Math.max(1, p.rack.levels);
        for (const c of p.rack.cells) {
          const cx = p.x + off.x + ((c.position ?? 1) - 1) * p.cw;
          const cy = p.y + off.y + (p.rack.levels - (c.level ?? 1)) * sh;
          if (cx + p.cw >= x0 && cx <= x1 && cy + sh >= y0 && cy <= y1) next.add(c.id);
        }
      }
      setPicked(next);
    }
    setMarquee(null);
  };
  const onPointerUp = () => {
    if (marquee) {
      finishMarquee();
      return;
    }
    if (!dragging) return;
    const p = placed.find((x) => x.rack.id === dragging.id);
    if (p && (Math.abs(dragging.dx) > 2 || Math.abs(dragging.dy) > 2)) {
      const [nx, ny] = settle(p, p.x + dragging.dx, p.y + dragging.dy);
      setOffsets({ ...offsets, [p.rack.id]: { x: nx - p.x, y: ny - p.y } });
      setLastMove({ id: p.rack.id, code: p.rack.code, x: p.rack.x === null ? null : m(p.rack.x), y: p.rack.y === null ? null : m(p.rack.y) });
      save.mutate({ id: p.rack.id, x: nx / PX_PER_M, y: ny / PX_PER_M });
    }
    setDragging(null);
  };

  const data = map.data;
  const zoneName = (id: string | null) => data?.zones.find((z) => z.id === id)?.name ?? "—";
  const stateName = (id: string | null) => data?.states.find((s) => s.id === id)?.name ?? "—";

  return (
    <div>
      <div className="wms-toolbar" style={{ marginBottom: 10 }}>
        <Select
          allowClear
          placeholder="Все помещения"
          style={{ minWidth: 200 }}
          value={roomId}
          onChange={setRoomId}
          options={(data?.rooms ?? []).map((r) => ({ value: r.id, label: `${r.code} — ${r.name} (${r.racks})` }))}
        />
        <Input.Search
          allowClear
          placeholder="Найти адрес, например A-01-05"
          style={{ width: 230 }}
          onSearch={(q) => {
            const needle = q.trim().toLowerCase();
            if (!needle || !data) return;
            const all = [
              ...data.racks.flatMap((r) => r.cells.map((c) => ({ c, r: r as MapRack | undefined }))),
              ...data.loose.map((c) => ({ c, r: undefined as MapRack | undefined })),
            ];
            const hit =
              all.find((x) => x.c.code.toLowerCase() === needle) ??
              all.find((x) => x.c.code.toLowerCase().startsWith(needle)) ??
              all.find((x) => x.c.code.toLowerCase().includes(needle));
            if (!hit) {
              message.warning(roomId ? `Адрес «${q}» не найден в выбранном помещении — снимите фильтр помещения` : `Адрес «${q}» на схеме не найден`);
              return;
            }
            if (real3d) {
              setFocus({ id: hit.c.id, n: Date.now() });
              return;
            }
            setSelected({ cell: hit.c, rack: hit.r });
            const p = placed.find((x) => x.rack.id === hit.r?.id);
            const canvas = canvasRef.current;
            if (p && canvas) {
              const off = offsets[p.rack.id] ?? { x: 0, y: 0 };
              const col = (hit.c.position ?? 1) - 1;
              const x = (p.x + off.x + col * p.cw + RULER + padX) * zoom;
              const y = (p.y + off.y + RULER + padY) * zoom;
              canvas.scrollTo({ left: Math.max(0, x - canvas.clientWidth / 2), top: Math.max(0, y - canvas.clientHeight / 2), behavior: "smooth" });
            }
          }}
        />
        <Segmented<Mode>
          value={mode}
          onChange={setMode}
          options={[
            { value: "fill", label: "Заполненность" },
            { value: "zone", label: "Зоны" },
            { value: "state", label: "Состояния" },
            { value: "capacity", label: "Вместимость" },
          ]}
        />
        <Segmented<View>
          value={view}
          onChange={setView}
          options={[
            { value: "plan", label: "2D план", icon: <BorderOutlined /> },
            { value: "iso", label: "3D вид", icon: <CodeSandboxOutlined /> },
          ]}
        />
        {view === "iso" && !webgl && (
          <Tooltip title="Повернуть на 90°">
            <Button icon={<RotateRightOutlined />} onClick={() => setRotation((r) => (r + 1) % 4)} />
          </Tooltip>
        )}
        {!real3d && (
        <Space.Compact>
          <Button icon={<ZoomOutOutlined />} onClick={() => setZoom((z) => Math.max(0.2, Math.round((z - 0.1) * 100) / 100))} />
          <Button style={{ width: 64, pointerEvents: "none" }}>{Math.round(zoom * 100)}%</Button>
          <Button icon={<ZoomInOutlined />} onClick={() => setZoom((z) => Math.min(3, Math.round((z + 0.1) * 100) / 100))} />
          <Tooltip title="Вписать всю схему в окно">
            <Button icon={<ExpandOutlined />} onClick={() => fit(false)} />
          </Tooltip>
        </Space.Compact>
        )}

        {can("topology.racks.write") && (
          <Tooltip title={view === "iso" ? "Перетаскивание доступно на 2D плане" : "Перетаскивайте стеллажи мышью; положение сохраняется в карточке стеллажа"}>
            <span className="wms-pill" style={{ padding: "4px 10px" }}>
              <DragOutlined />
              <Switch size="small" checked={arrange} disabled={view === "iso"} onChange={setArrange} />
              расставить стеллажи
            </span>
          </Tooltip>
        )}
        {arrange && lastMove && (
          <Button
            size="small"
            onClick={() => {
              save.mutate({ id: lastMove.id, x: lastMove.x, y: lastMove.y });
              setOffsets((o) => ({ ...o, [lastMove.id]: { x: 0, y: 0 } }));
              setLastMove(null);
            }}
          >
            Отменить перемещение {lastMove.code}
          </Button>
        )}
        {arrange && <span className="wms-pill">шаг сетки 0,5 м · Esc — отпустить на месте</span>}
        <Tooltip title="Зоны склада цветными областями с названиями — на плане и на полу в 3D. Область зоны задаётся в её карточке (X, Y, длина, ширина); без неё зона обводит свои стеллажи">
          <span className="wms-pill" style={{ padding: "4px 10px" }}>
            <Switch size="small" checked={showZones} onChange={setShowZones} />
            зоны на схеме
          </span>
        </Tooltip>
        {can("topology.locations.write") && view === "plan" && (
          <Tooltip title="Кликайте по ячейкам, чтобы выбрать несколько и изменить их разом; рамкой мышью тоже можно">
            <span className="wms-pill" style={{ padding: "4px 10px" }}>
              <SelectOutlined />
              <Switch size="small" checked={selectMode} onChange={setSelectMode} />
              выбрать несколько
            </span>
          </Tooltip>
        )}
        <span className="wms-toolbar-spacer" />
        {data && (
          <span className="wms-pill">
            ячеек <b>{data.cells}</b> · занято <b>{data.occupied}</b> · свободно <b>{data.cells - data.occupied}</b>
          </span>
        )}
        {currentRoom && m(currentRoom.length) > 0 && m(currentRoom.width) > 0 && (
          <span className="wms-pill">
            помещение <b>{fmtM(currentRoom.length)} × {fmtM(currentRoom.width)} м</b>
            {m(currentRoom.height) > 0 ? <> · высота <b>{fmtM(currentRoom.height)} м</b></> : null}
          </span>
        )}
        {picked.size > 0 && can("topology.locations.write") && (
          <Space size={4}>
            <Tag color="blue" style={{ margin: 0 }}>выбрано: {picked.size}</Tag>
            <Button size="small" type="primary" onClick={() => setBulkOpen(true)}>
              Изменить выбранные
            </Button>
            <Button size="small" onClick={() => setPicked(new Set())}>Снять выбор</Button>
          </Space>
        )}
      </div>

      <div className="wms-legend">
        <Popover
          title="Как читать схему"
          content={
            <div style={{ maxWidth: 360, fontSize: 13 }}>
              <p style={{ margin: "0 0 6px" }}>Сетка: одна клетка = 1 м, жирная линия = {MAJOR_M} м; координаты и размеры в метрах.</p>
              <p style={{ margin: "0 0 6px" }}>Клик по ячейке открывает её содержимое. Чтобы изменить несколько ячеек разом, включите «выбрать несколько» или обведите их рамкой.</p>
              <p style={{ margin: "0 0 6px" }}>3D вид: модель вращается на 360° левой кнопкой мыши (на планшете — пальцем), колесо или два пальца меняют масштаб, правая кнопка сдвигает. Клик по ячейке показывает её номер над ней.</p>
              {focusOn && <p style={{ margin: "0 0 6px" }}>Выберите зону, ячейку или ворота — в списках «Зона», «Ячейка», «Ворота» вверху слева или щелчком по модели, — чтобы приблизить. Esc — на уровень выше, «Весь склад» — общий вид; крошки показывают, что выбрано.</p>}
              {docksOn && <p style={{ margin: "0 0 6px" }}>Пандусы и ворота задаются в «Топология → Пандусы и ворота»: ворота встают на стену напротив зоны отгрузки, номер — над проёмом.</p>}
              <p style={{ margin: 0 }}>Переключатель «расставить стеллажи» разрешает перетаскивать стеллажи; положение сохраняется с шагом 0,5 м.</p>
            </div>
          }
        >
          <span style={{ cursor: "help" }}>
            <QuestionCircleOutlined /> как читать схему
          </span>
        </Popover>
        {defaultSized > 0 && (
          <Tooltip title="Задайте длину, ширину и высоту у типа места хранения: схема возьмёт их для ячеек этого типа. Пока используются 1,2 × 1,0 × 1,5 м.">
            <span style={{ color: "var(--wms-warning, #d98c1f)" }}>у {defaultSized} стеллаж(ей) размеры ячеек по умолчанию</span>
          </Tooltip>
        )}
        {data && data.rooms.some((r) => !(m(r.length) > 0 && m(r.width) > 0)) && (
          <Tooltip title="В карточке помещения заполните длину и ширину (и X/Y, если помещений несколько): на схеме появится контур зала.">
            <span style={{ color: "var(--wms-muted)" }}>у помещения без длины и ширины контур не рисуется</span>
          </Tooltip>
        )}
        {mode === "zone" &&
          (data?.zones ?? []).map((z) => (
            <span
              key={z.id}
              // ТЗ 5.1: in 3D a zone of the legend is a way to fly to it
              style={real3d && focusOn ? { cursor: "pointer" } : undefined}
              onClick={real3d && focusOn ? () => setFocus({ kind: "zone", id: z.id, n: Date.now() }) : undefined}
            >
              <i className="wms-dot" style={{ background: zoneColors.get(z.id) }} />
              {z.name}
            </span>
          ))}
        {mode === "fill" && (
          <>
            <span><i className="wms-dot" style={{ background: "var(--wms-cell-free)" }} />свободно</span>
            <span><i className="wms-dot" style={{ background: "#6f8fff" }} />занято (темнее = больше товара)</span>
            <span><i className="wms-dot" style={{ background: "#fa8c16" }} />всё отложено под заказы</span>
            <span><i className="wms-dot" style={{ background: "var(--wms-cell-blocked)" }} />заблокирована (неактивна)</span>
            <span><i className="wms-dot" style={{ background: "var(--wms-surface)", borderColor: "var(--wms-text)" }} />точка в углу — внутри паллета или короб</span>
          </>
        )}
        {mode === "capacity" && (
          <>
            <span><i className="wms-dot" style={{ background: "var(--wms-cell-empty)" }} />пусто</span>
            <span><i className="wms-dot" style={{ background: "#7cc47c" }} />заполнено частично (зеленее = свободнее)</span>
            <span><i className="wms-dot" style={{ background: "#fa8c16" }} />80 % и больше</span>
            <span><i className="wms-dot" style={{ background: "#cf1322" }} />100 % и больше</span>
            <span><i className="wms-dot" style={{ background: "var(--wms-cell-na)" }} />тип не контролирует вместимость</span>
          </>
        )}
        {mode === "state" && (
          <>
            <span><i className="wms-dot" style={{ background: "#52c41a" }} />свободна для работы</span>
            <span><i className="wms-dot" style={{ background: "#faad14" }} />только класть или только брать</span>
            <span><i className="wms-dot" style={{ background: "#ff4d4f" }} />заблокирована (ни класть, ни брать)</span>
          </>
        )}
      </div>

      {map.isLoading && (
        <div className="wms-card" style={{ padding: 40, textAlign: "center" }}>
          <Spin />
        </div>
      )}
      {/* UXW-71: a failed load is said in words, never an empty plan presented as loaded */}
      {map.isError && <LoadProblem error={map.error} onRetry={() => map.refetch()} />}
      {/* the drawn view without WebGL says why — with the 3D options on, or after the model failed to draw */}
      {data && placed.length > 0 && view === "iso" && !webgl && (!!sceneFailure || default3d || focusOn || docksOn) && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 10 }}
          data-testid="map3d-fallback"
          message={sceneFailure ? "3D-модель не запустилась — показан рисованный объёмный вид" : "3D-модель недоступна: браузер не поддерживает WebGL — показан рисованный объёмный вид"}
          description={
            sceneFailure
              ? "Видеокарта или браузер не дали нарисовать модель. Обновите страницу, чтобы попробовать снова. 2D план, поиск адреса и «Дерево и список» работают как обычно."
              : "Включите аппаратное ускорение в настройках браузера или откройте схему на другом компьютере. 2D план, поиск адреса и «Дерево и список» работают как обычно."
          }
          action={
            <Space direction="vertical" size={4}>
              <Button size="small" onClick={() => setView("plan")} data-testid="map3d-fallback-plan">
                Открыть 2D план
              </Button>
              {sceneFailure === "lost" && (
                <Button
                  size="small"
                  onClick={() => {
                    // one probe on demand: a WebGL context is never created on every render
                    const ok = hasWebGL();
                    setSceneFailure(ok ? null : "start");
                    setWebgl(ok);
                  }}
                >
                  Попробовать 3D снова
                </Button>
              )}
            </Space>
          }
        />
      )}
      {data && placed.length === 0 && (
        <div className="wms-card" style={{ padding: 40 }}>
          <Empty description="Стеллажей пока нет: создайте помещение и стеллажи, затем сгенерируйте ячейки" />
        </div>
      )}
      {data && placed.length > 0 && real3d && scene3d && (
        <SceneBoundary onError={() => dropScene("error")}>
          <Suspense
            fallback={
              <div className="wms-card" style={{ padding: 40, textAlign: "center" }}>
                <Spin />
              </div>
            }
          >
            <Warehouse3D
              scene={scene3d}
              colors={colors3d}
              showZones={showZones}
              focus={focus}
              focusMode={focusOn}
              gateInfo={gateInfo}
              zoneName={zoneName}
              onHover={setHover}
              onOpen={(cell, rack) => setSelected({ cell, rack })}
              onFail={dropScene}
            />
            {hover && <HoverCard hover={hover} zoneName={zoneName} stateName={stateName} />}
          </Suspense>
        </SceneBoundary>
      )}
      {data && placed.length > 0 && view === "iso" && isoScene && (
        <div className="wms-canvas" style={{ maxHeight: "70vh" }} ref={canvasRef}>
          <svg width={isoScene.w * zoom} height={isoScene.h * zoom} viewBox={`0 0 ${isoScene.w} ${isoScene.h}`} style={{ display: "block", userSelect: "none" }} onMouseLeave={() => setHover(null)}>
            <polygon points={poly(isoScene.corners, isoScene.dx, isoScene.dy)} fill={isoScene.floors.length ? "var(--wms-canvas)" : "var(--wms-surface-2)"} stroke="var(--wms-border)" />
            {isoScene.floors.map(({ room, rect }) => (
              <polygon key={`floor-${room.id}`} points={poly([iso(rect.x0, rect.y0, 0), iso(rect.x1, rect.y0, 0), iso(rect.x1, rect.y1, 0), iso(rect.x0, rect.y1, 0)], isoScene.dx, isoScene.dy)} fill="var(--wms-surface-2)" stroke="var(--wms-primary)" strokeOpacity={0.6} strokeDasharray="6 4" />
            ))}
            {showZones &&
              isoScene.zoneFloors.map(({ area, rect }) => (
                <polygon
                  key={`zone-floor-${area.zone.id}`}
                  points={poly([iso(rect.x0, rect.y0, 0), iso(rect.x1, rect.y0, 0), iso(rect.x1, rect.y1, 0), iso(rect.x0, rect.y1, 0)], isoScene.dx, isoScene.dy)}
                  fill={area.color}
                  fillOpacity={0.16}
                  stroke={area.color}
                  strokeOpacity={0.7}
                  strokeWidth={1.4}
                  pointerEvents="none"
                />
              ))}
            {isoScene.docks.map(({ layout, ramp, edge, doors }) => {
              const drop = layout.rampHeight * PX_PER_M;
              return (
                <g key={`iso-dock-${layout.dock.id}`}>
                  {ramp && (
                    <polygon points={poly([iso(ramp.x0, ramp.y0, 0), iso(ramp.x1, ramp.y0, 0), iso(ramp.x1, ramp.y1, 0), iso(ramp.x0, ramp.y1, 0)], isoScene.dx, isoScene.dy)} fill="var(--wms-3d-dock)" fillOpacity={0.85} stroke="var(--wms-rack-line)" pointerEvents="none" />
                  )}
                  {drop > 0 && (
                    <polygon points={poly([iso(edge.x0, edge.y0, 0), iso(edge.x1, edge.y1, 0), iso(edge.x1, edge.y1, -drop), iso(edge.x0, edge.y0, -drop)], isoScene.dx, isoScene.dy)} fill="var(--wms-3d-dock)" stroke="var(--wms-rack-line)" pointerEvents="none" />
                  )}
                  {doors.map(({ gate, seg }) => {
                    const h = layout.doorHeight * PX_PER_M;
                    const [tx, ty] = iso((seg.x0 + seg.x1) / 2, (seg.y0 + seg.y1) / 2, h + 0.6 * PX_PER_M);
                    const cell = gateCells[gate.location_id];
                    const hover = (e: React.MouseEvent) => cell && setHover({ cell, x: e.clientX, y: e.clientY, label: `Ворота ${gate.number} · ${layout.dock.name}` });
                    return (
                      <g key={gate.location_id} style={{ cursor: cell ? "pointer" : undefined }} onMouseEnter={hover} onMouseMove={hover} onMouseLeave={() => setHover(null)} onClick={() => cell && setSelected({ cell })}>
                        <polygon points={poly([iso(seg.x0, seg.y0, 0), iso(seg.x1, seg.y1, 0), iso(seg.x1, seg.y1, h), iso(seg.x0, seg.y0, h)], isoScene.dx, isoScene.dy)} fill="var(--wms-3d-door)" stroke="var(--wms-primary)" strokeWidth={1.5} />
                        <text x={tx + isoScene.dx} y={ty + isoScene.dy} fontSize={13} fontWeight={700} fill="var(--wms-primary)" textAnchor="middle" stroke="var(--wms-surface)" strokeWidth={3} paintOrder="stroke">
                          {gate.number}
                        </text>
                      </g>
                    );
                  })}
                </g>
              );
            })}
            {Array.from({ length: Math.floor(isoScene.sceneW / PX_PER_M) }, (_, i) => (i + 1) * PX_PER_M).map((gx, i) => (
              <line key={`gx${gx}`} x1={iso(gx, 0, 0)[0] + isoScene.dx} y1={iso(gx, 0, 0)[1] + isoScene.dy} x2={iso(gx, isoScene.sceneH, 0)[0] + isoScene.dx} y2={iso(gx, isoScene.sceneH, 0)[1] + isoScene.dy} stroke="var(--wms-grid)" strokeOpacity={(i + 1) % MAJOR_M === 0 ? 1 : 0.45} />
            ))}
            {Array.from({ length: Math.floor(isoScene.sceneH / PX_PER_M) }, (_, i) => (i + 1) * PX_PER_M).map((gy, i) => (
              <line key={`gy${gy}`} x1={iso(0, gy, 0)[0] + isoScene.dx} y1={iso(0, gy, 0)[1] + isoScene.dy} x2={iso(isoScene.sceneW, gy, 0)[0] + isoScene.dx} y2={iso(isoScene.sceneW, gy, 0)[1] + isoScene.dy} stroke="var(--wms-grid)" strokeOpacity={(i + 1) % MAJOR_M === 0 ? 1 : 0.45} />
            ))}
            {Array.from({ length: Math.floor(isoScene.sceneW / (PX_PER_M * MAJOR_M)) }, (_, i) => (i + 1) * MAJOR_M).map((metres) => {
              const [tx, ty] = iso(metres * PX_PER_M, isoScene.sceneH, 0);
              return (
                <text key={`tx${metres}`} x={tx + isoScene.dx} y={ty + isoScene.dy + 12} fontSize={10} fill="var(--wms-muted)" textAnchor="middle">
                  {metres} м
                </text>
              );
            })}
            {isoScene.floors.map(({ room, rect }) => {
              const [lx, ly] = iso(rect.x0, rect.y0, 0);
              return (
                <text key={`floor-label-${room.id}`} x={lx + isoScene.dx + 4} y={ly + isoScene.dy - 4} fontSize={11} fill="var(--wms-muted)">
                  {room.code} · {fmtM(room.length)} × {fmtM(room.width)} м
                </text>
              );
            })}
            {isoScene.racks.map(({ rack, rect }) => (
              <polygon key={`foot-${rack.id}`} points={poly([iso(rect.x0, rect.y0, 0), iso(rect.x1, rect.y0, 0), iso(rect.x1, rect.y1, 0), iso(rect.x0, rect.y1, 0)], isoScene.dx, isoScene.dy)} fill="var(--wms-surface)" stroke="var(--wms-cell-na)" />
            ))}
            {isoScene.items.map((item) => {
              const P = (x: number, y: number, z: number) => iso(x, y, z);
              const box = ({ cell, rack, rect, z0, z1 }: { cell: MapCell; rack?: MapRack; rect: Rect; z0: number; z1: number }) => {
                const color = cellFill(cell, mode, zoneColors, data.states, maxQty);
                const top = poly([P(rect.x0, rect.y0, z1), P(rect.x1, rect.y0, z1), P(rect.x1, rect.y1, z1), P(rect.x0, rect.y1, z1)], isoScene.dx, isoScene.dy);
                const front = poly([P(rect.x0, rect.y1, z0), P(rect.x1, rect.y1, z0), P(rect.x1, rect.y1, z1), P(rect.x0, rect.y1, z1)], isoScene.dx, isoScene.dy);
                const right = poly([P(rect.x1, rect.y0, z0), P(rect.x1, rect.y1, z0), P(rect.x1, rect.y1, z1), P(rect.x1, rect.y0, z1)], isoScene.dx, isoScene.dy);
                const isSelected = selected?.cell.id === cell.id;
                return (
                  <g
                    key={cell.id}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={(e) => setHover({ cell, rack, x: e.clientX, y: e.clientY })}
                    onMouseMove={(e) => setHover({ cell, rack, x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => setHover(null)}
                    onClick={() => setSelected({ cell, rack })}
                  >
                    <polygon points={front} fill={color} stroke="var(--wms-cell-stroke)" strokeWidth={0.6} />
                    <polygon points={right} fill={color} stroke="var(--wms-cell-stroke)" strokeWidth={0.6} />
                    <polygon points={right} fill="#000" opacity={0.22} />
                    <polygon points={top} fill={color} stroke={isSelected ? "var(--wms-text)" : "var(--wms-cell-stroke)"} strokeWidth={isSelected ? 1.5 : 0.6} />
                    <polygon points={top} fill="#fff" opacity={0.3} pointerEvents="none" />
                  </g>
                );
              };
              if (item.kind === "floor") return box(item.box);
              // the frame of the rack: translucent faces, a line per level and per bay — the cells are drawn into it
              const { rect, height: h, rack } = item;
              const levels = Math.max(1, rack.levels);
              const bays = Math.max(1, rack.positions);
              const at = (x: number, y: number, z: number) => {
                const [sx, sy] = P(x, y, z);
                return `${(sx + isoScene.dx).toFixed(1)},${(sy + isoScene.dy).toFixed(1)}`;
              };
              let lines = "";
              for (let l = 1; l <= levels; l++) {
                const z = (l * h) / levels;
                lines += `M${at(rect.x0, rect.y1, z)}L${at(rect.x1, rect.y1, z)}M${at(rect.x1, rect.y0, z)}L${at(rect.x1, rect.y1, z)}`;
              }
              for (let i = 0; i <= bays; i++) {
                if (item.alongX) {
                  const x = rect.x0 + ((rect.x1 - rect.x0) * i) / bays;
                  lines += `M${at(x, rect.y1, 0)}L${at(x, rect.y1, h)}`;
                } else {
                  const y = rect.y0 + ((rect.y1 - rect.y0) * i) / bays;
                  lines += `M${at(rect.x1, y, 0)}L${at(rect.x1, y, h)}`;
                }
              }
              const front = poly([P(rect.x0, rect.y1, 0), P(rect.x1, rect.y1, 0), P(rect.x1, rect.y1, h), P(rect.x0, rect.y1, h)], isoScene.dx, isoScene.dy);
              const right = poly([P(rect.x1, rect.y0, 0), P(rect.x1, rect.y1, 0), P(rect.x1, rect.y1, h), P(rect.x1, rect.y0, h)], isoScene.dx, isoScene.dy);
              const top = poly([P(rect.x0, rect.y0, h), P(rect.x1, rect.y0, h), P(rect.x1, rect.y1, h), P(rect.x0, rect.y1, h)], isoScene.dx, isoScene.dy);
              return (
                <g key={`rack-${rack.id}`}>
                  <g pointerEvents="none">
                    <polygon points={front} fill="var(--wms-rack-frame)" fillOpacity={0.3} />
                    <polygon points={right} fill="var(--wms-rack-frame)" fillOpacity={0.6} />
                    <polygon points={top} fill="var(--wms-rack-frame)" fillOpacity={0.3} stroke="var(--wms-rack-line)" strokeWidth={0.8} />
                    <path d={lines} stroke="var(--wms-rack-line)" strokeWidth={0.7} fill="none" />
                  </g>
                  {item.boxes.map(box)}
                </g>
              );
            })}
            {isoScene.racks.map(({ rack, rect, height: rh }) => {
              const [lx, ly] = iso(rect.x0, rect.y0, rh + 6);
              return (
                <text key={`label-${rack.id}`} x={lx + isoScene.dx} y={ly + isoScene.dy} fontSize={11} fontWeight={600} fill="var(--wms-text)">
                  {rack.code}
                </text>
              );
            })}
            {showZones &&
              isoScene.zoneFloors.map(({ area, rect }) => {
                // a map label: about 15 px on the screen whatever the zoom, at the front corner of the zone's floor
                // a label in the right half of the scene runs to the left of its corner, so it never leaves the canvas
                const [lx, ly] = iso(rect.x0 + 0.6 * PX_PER_M, rect.y1 - 0.6 * PX_PER_M, 0);
                const size = Math.min(180, Math.max(14, 15 / zoom));
                const rightHalf = lx + isoScene.dx > isoScene.w / 2;
                return (
                  <text
                    key={`zone-label-${area.zone.id}`}
                    x={lx + isoScene.dx}
                    y={ly + isoScene.dy}
                    fontSize={size}
                    fontWeight={700}
                    fill={area.color}
                    stroke="var(--wms-surface)"
                    strokeWidth={size / 4}
                    paintOrder="stroke"
                    textAnchor={rightHalf ? "end" : "start"}
                    pointerEvents="none"
                  >
                    {area.zone.name}
                  </text>
                );
              })}
          </svg>
          {hover && <HoverCard hover={hover} zoneName={zoneName} stateName={stateName} />}
        </div>
      )}
      {data && placed.length > 0 && view === "plan" && (
        <div className="wms-canvas" style={{ maxHeight: "70vh" }} ref={canvasRef}>
          <svg
            ref={svgRef}
            width={(width + RULER + padX) * zoom}
            height={(height + RULER + padY) * zoom}
            viewBox={`${-RULER - padX} ${-RULER - padY} ${width + RULER + padX} ${height + RULER + padY}`}
            style={{ display: "block", cursor: arrange ? "grab" : "default", userSelect: "none" }}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={() => {
              setHover(null);
              if (dragging) onPointerUp();
            }}
          >
            <defs>
              <pattern id="grid1" width={PX_PER_M} height={PX_PER_M} patternUnits="userSpaceOnUse">
                <path d={`M ${PX_PER_M} 0 L 0 0 0 ${PX_PER_M}`} fill="none" stroke="var(--wms-grid)" strokeWidth={0.6} />
              </pattern>
              <pattern id="grid5" width={PX_PER_M * MAJOR_M} height={PX_PER_M * MAJOR_M} patternUnits="userSpaceOnUse">
                <path d={`M ${PX_PER_M * MAJOR_M} 0 L 0 0 0 ${PX_PER_M * MAJOR_M}`} fill="none" stroke="var(--wms-grid)" strokeWidth={1.4} />
              </pattern>
              <pattern id="dockHatch" width={8} height={8} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                <line x1={0} y1={0} x2={0} y2={8} stroke="var(--wms-rack-line)" strokeWidth={2} />
              </pattern>
              <marker id="dockArrow" viewBox="0 0 10 10" refX={9} refY={5} markerWidth={7} markerHeight={7} orient="auto-start-reverse">
                <path d="M0 0L10 5L0 10z" fill="var(--wms-text)" />
              </marker>
              <filter id="rackShadow" x="-5%" y="-5%" width="110%" height="115%">
                <feDropShadow dx="0" dy="1.5" stdDeviation="1.5" floodColor="#101828" floodOpacity="0.12" />
              </filter>
            </defs>
            <rect x={-RULER - padX} y={-RULER - padY} width={width + RULER + padX} height={height + RULER + padY} fill="var(--wms-surface)" onPointerDown={onBackgroundDown} />
            <rect width={width} height={height} fill="url(#grid1)" onPointerDown={onBackgroundDown} />
            <rect width={width} height={height} fill="url(#grid5)" pointerEvents="none" />
            <line x1={0} y1={0} x2={width} y2={0} stroke="var(--wms-border)" pointerEvents="none" />
            <line x1={0} y1={0} x2={0} y2={height} stroke="var(--wms-border)" pointerEvents="none" />
            {roomRects.map((r) => (
              <g key={`room-${r.room.id}`} pointerEvents="none">
                <rect x={r.x} y={r.y} width={r.w} height={r.h} rx={4} fill="var(--wms-surface)" fillOpacity={0.55} stroke="var(--wms-primary)" strokeOpacity={0.6} strokeWidth={1.5} strokeDasharray="8 4" />
                <text x={r.x + 8} y={r.y + 16} fontSize={12} fontWeight={600} fill="var(--wms-muted)">
                  {r.room.code} — {r.room.name} · {fmtM(r.room.length)} × {fmtM(r.room.width)} м
                </text>
              </g>
            ))}
            {showZones &&
              zoneAreas.map((a) => (
                <rect key={`zone-${a.zone.id}`} x={a.x} y={a.y} width={a.w} height={a.h} rx={8} fill={a.color} fillOpacity={0.08} stroke={a.color} strokeOpacity={0.6} strokeWidth={1.6} pointerEvents="none" />
              ))}
            {floorPlaces.map(({ cell, x, y, w, h }) => {
              const isSelected = selected?.cell.id === cell.id;
              return (
                <g key={`floor-${cell.id}`}>
                  <rect
                    x={x}
                    y={y}
                    width={w}
                    height={h}
                    rx={3}
                    fill={cellFill(cell, mode, zoneColors, data.states, maxQty)}
                    stroke={isSelected ? "var(--wms-text)" : cell.color ?? "#98a2b3"}
                    strokeWidth={isSelected ? 2 : 1}
                    filter="url(#rackShadow)"
                    style={{ cursor: "pointer" }}
                    onMouseEnter={(e) => setHover({ cell, x: e.clientX, y: e.clientY })}
                    onMouseMove={(e) => setHover({ cell, x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => setHover(null)}
                    onClick={() => !arrange && setSelected({ cell })}
                  />
                  <text x={x + w / 2} y={y + h / 2 + 3.5} fontSize={10} fontWeight={600} fill="var(--wms-text)" textAnchor="middle" pointerEvents="none">
                    {fitLabel(cell.code, w - 6, 6)}
                  </text>
                </g>
              );
            })}
            {dockLayouts.map((l) => (
              <DockPlan key={`dock-${l.dock.id}`} layout={l} cells={gateCells} onHover={setHover} onOpen={(cell) => !arrange && setSelected({ cell })} />
            ))}
            {Array.from({ length: Math.floor(width / (PX_PER_M * MAJOR_M)) }, (_, i) => (i + 1) * MAJOR_M).map((metres) => (
              <text key={`rx${metres}`} x={metres * PX_PER_M} y={-5} fontSize={9} fill="var(--wms-muted)" textAnchor="middle" pointerEvents="none">
                {metres} м
              </text>
            ))}
            {Array.from({ length: Math.floor(height / (PX_PER_M * MAJOR_M)) }, (_, i) => (i + 1) * MAJOR_M).map((metres) => (
              <text key={`ry${metres}`} x={-3} y={metres * PX_PER_M + 3} fontSize={9} fill="var(--wms-muted)" textAnchor="end" pointerEvents="none">
                {metres} м
              </text>
            ))}
            {placed.map((p) => {
              const drag = dragging?.id === p.rack.id ? { x: dragging.dx, y: dragging.dy } : { x: 0, y: 0 };
              const off = offsets[p.rack.id] ?? { x: 0, y: 0 };
              const x = p.x + off.x + drag.x;
              const y = p.y + off.y + drag.y;
              const sh = p.d / Math.max(1, p.rack.levels);
              return (
                <g key={p.rack.id} transform={`translate(${x}, ${y})`} onPointerDown={(e) => onPointerDown(p, e)} style={arrange ? { cursor: dragging?.id === p.rack.id ? "grabbing" : "grab" } : undefined}>
                  <text x={0} y={-4} fontSize={11} fill="var(--wms-text)" fontWeight={600}>
                    <title>{rackLabel(p.rack)}</title>
                    {fitLabel(rackLabel(p.rack), p.w)}
                  </text>
                  {dragging?.id === p.rack.id && (
                    <text x={0} y={p.d + 12} fontSize={10} fill="#1677ff" pointerEvents="none">
                      {(x / PX_PER_M).toFixed(1)} × {(y / PX_PER_M).toFixed(1)} м
                    </text>
                  )}
                  <rect width={p.w} height={p.d} rx={2} fill="var(--wms-surface)" stroke={p.auto ? "#b9c0cc" : "#98a2b3"} strokeDasharray={p.auto ? "4 3" : undefined} filter="url(#rackShadow)" />
                  {p.rack.cells.map((c) => {
                    const col = (c.position ?? 1) - 1;
                    const row = p.rack.levels - (c.level ?? 1);
                    const cx = col * p.cw;
                    const cy = row * sh;
                    const isSelected = selected?.cell.id === c.id;
                    const isPicked = picked.has(c.id);
                    return (
                      <g key={c.id}>
                        <rect
                          x={cx + 0.5}
                          y={cy + 0.5}
                          width={Math.max(1, p.cw - 1)}
                          height={Math.max(1, sh - 1)}
                          rx={1}
                          fill={cellFill(c, mode, zoneColors, data.states, maxQty)}
                          stroke={isPicked ? "#1677ff" : isSelected ? "var(--wms-text)" : c.color ?? "var(--wms-cell-stroke)"}
                          strokeWidth={isPicked || isSelected ? 2 : c.color ? 1.5 : 0.6}
                          style={{ cursor: "pointer" }}
                          onMouseEnter={(e) => setHover({ cell: c, rack: p.rack, x: e.clientX, y: e.clientY })}
                          onMouseMove={(e) => setHover({ cell: c, rack: p.rack, x: e.clientX, y: e.clientY })}
                          onMouseLeave={() => setHover(null)}
                          onClick={(e) => {
                            if (arrange) return;
                            if (selectMode || e.shiftKey || e.ctrlKey || e.metaKey) {
                              const next = new Set(picked);
                              if (next.has(c.id)) next.delete(c.id);
                              else next.add(c.id);
                              setPicked(next);
                              return;
                            }
                            setSelected({ cell: c, rack: p.rack });
                          }}
                        />
                        {c.nested > 0 && sh >= 8 && <circle cx={cx + p.cw - 4} cy={cy + 4} r={2.5} fill="var(--wms-surface)" stroke="var(--wms-text)" strokeWidth={1} pointerEvents="none" />}
                      </g>
                    );
                  })}
                </g>
              );
            })}
            {showZones &&
              zoneAreas.map((a) => {
                // the name lies over its area as on a map: large and translucent over a big hall, smaller in a strip,
                // below the floor places of the zone; the racks stay visible through it
                const note = zoneNote(a.zone);
                const size = Math.max(13, Math.min(56, (a.w * 0.85) / (a.zone.name.length * 0.58), a.h * 0.16));
                const places = floorPlaces.filter((f) => f.cell.zone_id === a.zone.id);
                const below = places.length ? Math.max(...places.map((f) => f.y + f.h)) - a.y : 0;
                const cy = a.y + Math.max(a.h / 2, below + size + 8);
                const opacity = size > 30 ? 0.42 : 0.9;
                return (
                  <g key={`zone-label-${a.zone.id}`} pointerEvents="none">
                    <text x={a.x + a.w / 2} y={cy} fontSize={size} fontWeight={700} fill={a.color} fillOpacity={opacity} textAnchor="middle" stroke="var(--wms-surface)" strokeOpacity={opacity} strokeWidth={size > 30 ? 0 : 3} paintOrder="stroke" letterSpacing={0.3}>
                      <title>{a.zone.name}</title>
                      {a.zone.name}
                    </text>
                    {note && (
                      <text x={a.x + a.w / 2} y={cy + Math.max(13, size * 0.45)} fontSize={Math.max(11, size * 0.38)} fill={a.color} fillOpacity={opacity} textAnchor="middle">
                        {note}
                      </text>
                    )}
                  </g>
                );
              })}
            {marquee && (
              <rect
                x={Math.min(marquee.x0, marquee.x1)}
                y={Math.min(marquee.y0, marquee.y1)}
                width={Math.abs(marquee.x1 - marquee.x0)}
                height={Math.abs(marquee.y1 - marquee.y0)}
                fill="rgba(22,119,255,0.12)"
                stroke="#1677ff"
                strokeDasharray="4 3"
                pointerEvents="none"
              />
            )}
          </svg>
          {hover && <HoverCard hover={hover} zoneName={zoneName} stateName={stateName} />}
          <Typography.Text type="secondary" style={{ display: "block", padding: "4px 8px" }}>
            Вид сверху в метрах: ширина стеллажа = позиции × длина ячейки, глубина = глубина стеллажа или ширина ячейки; ярусы — полосы внутри
            (нижний ярус снизу). Чтобы изменить несколько ячеек разом, включите «выбрать несколько» и кликайте по ним или обведите их рамкой.
          </Typography.Text>
        </div>
      )}
      <Modal title={`Изменить ${picked.size} ячеек`} open={bulkOpen} onCancel={() => setBulkOpen(false)} okText="Применить" cancelText="Отмена" confirmLoading={bulk.isPending} onOk={() => bulkForm.submit()} destroyOnHidden>
        <Form form={bulkForm} layout="vertical" onFinish={(v) => bulk.mutate(Object.fromEntries(Object.entries(v).filter(([, val]) => val !== undefined && val !== "")))}>
          <Form.Item name="zone_id" label="Зона">
            <Select allowClear options={(data?.zones ?? []).map((z) => ({ value: z.id, label: `${z.code} — ${z.name}` }))} />
          </Form.Item>
          <Form.Item name="state_id" label="Состояние">
            <Select allowClear options={(data?.states ?? []).map((st) => ({ value: st.id, label: st.name }))} />
          </Form.Item>
          <Form.Item name="is_active" label="Активность">
            <Select allowClear options={[{ value: true, label: "Активна" }, { value: false, label: "Неактивна" }]} />
          </Form.Item>
          <Form.Item name="color" label="Цвет на схеме (#hex)">
            <Input placeholder="#ff8800" maxLength={16} />
          </Form.Item>
          <Form.Item name="clear_color" valuePropName="checked" label="Убрать цвет">
            <Switch />
          </Form.Item>
          <Typography.Text type="secondary">Пустые поля не меняются.</Typography.Text>
        </Form>
      </Modal>
      {data && unplacedLoose.length > 0 && (
        <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
          Вне стеллажей и без области зоны на схеме: {unplacedLoose.map((c) => c.code).join(", ")}. Задайте область их зоны (X, Y, длина, ширина в
          карточке зоны) — они встанут внутри неё.
        </Typography.Paragraph>
      )}

      <Drawer open={!!selected} onClose={() => setSelected(null)} width={640} title={selected ? `Ячейка ${selected.cell.code}` : ""}>
        {selected && (
          <>
            <Descriptions size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Стеллаж">{selected.rack ? <Link to={`/racks/${selected.rack.id}`}>{selected.rack.code}</Link> : "—"}</Descriptions.Item>
              <Descriptions.Item label="Ярус / позиция">{selected.cell.level ?? "—"} / {selected.cell.position ?? "—"}</Descriptions.Item>
              <Descriptions.Item label="Зона">{zoneName(selected.cell.zone_id)}</Descriptions.Item>
              <Descriptions.Item label="Состояние">{stateName(selected.cell.state_id)}</Descriptions.Item>
              <Descriptions.Item label="На складе">{Number(selected.cell.quantity)}</Descriptions.Item>
              <Descriptions.Item label="Отложено под заказы">{Number(selected.cell.planned_out)}</Descriptions.Item>
              <Descriptions.Item label="Можно взять">{Math.max(0, Number(selected.cell.quantity) - Number(selected.cell.planned_out))}</Descriptions.Item>
              {(() => {
                const st = data?.states.find((x) => x.id === selected.cell.state_id);
                const reasons = [
                  !selected.cell.is_active ? "ячейка неактивна" : null,
                  st && !st.allow_put ? `состояние «${st.name}» запрещает класть` : null,
                  st && !st.allow_pick ? `состояние «${st.name}» запрещает брать` : null,
                ].filter(Boolean);
                return reasons.length ? <Descriptions.Item label="Почему заблокирована">{reasons.join("; ")}</Descriptions.Item> : null;
              })()}
            </Descriptions>
            <Space style={{ marginBottom: 12 }}>
              <Link to={`/locations/${selected.cell.id}`}>
                <Button type="primary">Открыть карточку ячейки</Button>
              </Link>
            </Space>
            {contentsQuery.data && contentsQuery.data.nested.length > 0 && (
              <>
                <Typography.Title level={5}>Вложенные МХ</Typography.Title>
                <ul style={{ paddingLeft: 18 }}>
                  {contentsQuery.data.nested.map((n) => (
                    <li key={n.id}>
                      <Link to={`/locations/${n.id}`}>{n.code}</Link> — {n.type}, остаток {Number(n.quantity)}
                      {n.objects ? ` (товаров: ${n.objects})` : ""}
                      {n.children ? `, внутри ещё МХ: ${n.children}` : ""}
                    </li>
                  ))}
                </ul>
              </>
            )}
            <Typography.Title level={5}>Что лежит непосредственно в ячейке</Typography.Title>
            <EntityTable key={selected.cell.id} entity="stock" embedded baseFilter={{ field: "location_id", op: "eq", value: selected.cell.id }} hideFields={["location_id", "warehouse_id"]} />
          </>
        )}
      </Drawer>
    </div>
  );
}
